import type { AllCSSProperties, RefObject } from 'preact';
import { useState } from 'preact/hooks';
import type { MoveTo } from '../types';
import { getFixedHeaderZIndex } from '@/widgets/utils/layout';

export const OVER_DIALOG_ZINDEX = 10000;
export const OFFSET = 20;

/**
 * This hook provides the popover’s position controller, exposing methods like
 * `moveTo.topCenter()`, `moveTo.bottomCenter()`, `moveTo.leftCenter()`, etc, for
 * external adjustments. It returns the current position after movement and
 * generates the necessary CSS styles to visually apply the position.
 *
 * Note that while it manages and supplies all position-related logic
 * (e.g., coordinates and styles), it does not set positions directly—that
 * responsibility lies with another hook or component.
 */
export function usePopoverPosition(targetRef: RefObject<HTMLElement>) {
  const [movedPosition, setMovedPosition] = useState('topCenter');
  const [popoverStyle, setPopoverStyle] = useState<AllCSSProperties>({});
  const el = targetRef.current as HTMLElement;

  function update(
    position: string,
    callback: (rect: DOMRect) => AllCSSProperties
  ) {
    setMovedPosition(position);
    setPopoverStyle((prev) => {
      if (!targetRef.current) return prev; // same object ref = no-rerender
      const rect = targetRef.current.getBoundingClientRect();
      const styles = callback(rect);

      // Only update styles if there's an actual change to prevent unnecessary re-renders
      return Object.keys(styles).some((key) => prev[key] !== styles[key])
        ? {
            zIndex: targetRef.current.closest('dialog')
              ? OVER_DIALOG_ZINDEX
              : getFixedHeaderZIndex() + 1,
            ...styles
          }
        : prev; // same object ref = no-rerender
    });
  }

  const moveTo: MoveTo = {
    topCenter: () =>
      update('topCenter', (r) => ({
        bottom: window.innerHeight - r.top - getScrollY(el) + OFFSET,
        left: r.left + r.width / 2 + window.scrollX,
        transform: `translateX(-50%)`
      })),
    bottomCenter: () =>
      update('bottomCenter', (r) => ({
        top: r.bottom + getScrollY(el) + OFFSET,
        left: r.left + r.width / 2 + window.scrollX,
        transform: `translateX(-50%)`
      })),
    leftCenter: () =>
      update('leftCenter', (r) => ({
        top: r.top + r.height / 2 + getScrollY(el),
        right: window.innerWidth - r.left - window.scrollX + OFFSET,
        transform: `translateY(-50%)`
      })),
    rightCenter: () =>
      update('rightCenter', (r) => ({
        top: r.top + r.height / 2 + getScrollY(el),
        left: r.right + window.scrollX + OFFSET,
        transform: `translateY(-50%)`
      })),
    topRight: () =>
      update('topRight', (r) => ({
        bottom: window.innerHeight - r.top - getScrollY(el) + OFFSET,
        left: r.left + window.scrollX
      })),
    topLeft: () =>
      update('topLeft', (r) => ({
        bottom: window.innerHeight - r.top - getScrollY(el) + OFFSET,
        right: window.innerWidth - r.right - window.scrollX
      })),
    bottomRight: () =>
      update('bottomRight', (r) => ({
        top: r.bottom + getScrollY(el) + OFFSET,
        left: r.left + window.scrollX
      })),
    bottomLeft: () =>
      update('bottomLeft', (r) => ({
        top: r.bottom + getScrollY(el) + OFFSET,
        right: window.innerWidth - r.right - window.scrollX
      })),
    leftBottom: () =>
      update('leftBottom', (r) => ({
        top: r.top + getScrollY(el),
        right: window.innerWidth - r.left - window.scrollX + OFFSET
      })),
    leftTop: () =>
      update('leftTop', (r) => ({
        bottom: window.innerHeight - r.bottom - getScrollY(el),
        right: window.innerWidth - r.left - window.scrollX + OFFSET
      })),
    rightBottom: () =>
      update('rightBottom', (r) => ({
        top: r.top + getScrollY(el),
        left: r.right + window.scrollX + OFFSET
      })),
    rightTop: () =>
      update('rightTop', (r) => ({
        bottom: window.innerHeight - r.bottom - getScrollY(el),
        left: r.right + window.scrollX + OFFSET
      }))
  };

  return { popoverStyle, moveTo, movedPosition };
}

function getScrollY(element: HTMLElement) {
  const dialog = element?.closest('dialog[open]');
  return dialog ? 0 : window.scrollY;
}
